import type { AIModel } from '@/types';

export type CollabWorkflow = 'standard' | 'deep_research';
export type StandardCollabNodeRole = 'start' | 'drafter' | 'editor' | 'output';
export type DeepResearchNodeRole =
  | 'start'
  | 'orchestrator'
  | 'planner'
  | 'web_research'
  | 'academic_research'
  | 'code_analysis'
  | 'source_verification'
  | 'timeline_builder'
  | 'citation_manager'
  | 'report_writer'
  | 'knowledge_graph'
  | 'output';
export type CollabNodeRole = StandardCollabNodeRole | DeepResearchNodeRole;
export type StandardCollabMessageRole = 'user_prompt' | 'drafter' | 'editor';
export type DeepResearchMessageRole =
  | 'user_prompt'
  | 'orchestrator'
  | 'planner'
  | 'web_research'
  | 'academic_research'
  | 'code_analysis'
  | 'source_verification'
  | 'timeline_builder'
  | 'citation_manager'
  | 'report_writer'
  | 'knowledge_graph';
export type CollabMessageRole = StandardCollabMessageRole | DeepResearchMessageRole;
export type CollabMessageStatus = 'pending' | 'running' | 'complete' | 'error';

export interface CollabNodeConfig {
  id: string;
  role: CollabNodeRole;
  modelId?: string | null;
  position: {
    x: number;
    y: number;
  };
}

export interface CollabConfig {
  version: 2;
  workflow: CollabWorkflow;
  nodes: CollabNodeConfig[];
}

export interface CollabRoleDefinition {
  role: CollabNodeRole;
  title: string;
  description: string;
  shortLabel: string;
  requiresModel: boolean;
  required: boolean;
}

const STANDARD_NODE_ORDER: CollabNodeRole[] = ['start', 'drafter', 'editor', 'output'];
export const DEEP_RESEARCH_ROLE_ORDER: Array<Exclude<DeepResearchMessageRole, 'user_prompt'>> = [
  'orchestrator',
  'planner',
  'web_research',
  'academic_research',
  'code_analysis',
  'source_verification',
  'timeline_builder',
  'citation_manager',
  'report_writer',
  'knowledge_graph',
];

const ROLE_DEFINITIONS: Record<CollabNodeRole, CollabRoleDefinition> = {
  start: {
    role: 'start',
    title: 'User Prompt',
    description: 'The research request that starts the workflow.',
    shortLabel: 'Input',
    requiresModel: false,
    required: true,
  },
  drafter: {
    role: 'drafter',
    title: 'Drafter',
    description: 'Produces an independent candidate answer.',
    shortLabel: 'Draft',
    requiresModel: true,
    required: true,
  },
  editor: {
    role: 'editor',
    title: 'Editor',
    description: 'Combines drafts into a polished final answer.',
    shortLabel: 'Edit',
    requiresModel: true,
    required: true,
  },
  output: {
    role: 'output',
    title: 'Final Output',
    description: 'Publishes the completed response back into chat.',
    shortLabel: 'Output',
    requiresModel: false,
    required: true,
  },
  orchestrator: {
    role: 'orchestrator',
    title: 'Orchestrator',
    description: 'Coordinates the run, manages fallbacks, and summarizes completion.',
    shortLabel: 'Orchestrate',
    requiresModel: true,
    required: true,
  },
  planner: {
    role: 'planner',
    title: 'Planner Agent',
    description: 'Breaks the request into questions, evidence needs, and workstreams.',
    shortLabel: 'Plan',
    requiresModel: true,
    required: true,
  },
  web_research: {
    role: 'web_research',
    title: 'Web Research Agent',
    description: 'Collects broad web evidence, recency signals, and external references.',
    shortLabel: 'Web',
    requiresModel: true,
    required: true,
  },
  academic_research: {
    role: 'academic_research',
    title: 'Academic Paper Agent',
    description: 'Looks for papers, formal studies, and higher-rigor sources.',
    shortLabel: 'Academic',
    requiresModel: true,
    required: true,
  },
  code_analysis: {
    role: 'code_analysis',
    title: 'Code Analysis Agent',
    description: 'Analyzes codebase and project context relevant to the request.',
    shortLabel: 'Code',
    requiresModel: true,
    required: true,
  },
  source_verification: {
    role: 'source_verification',
    title: 'Source Verification Agent',
    description: 'Cross-checks claims against known evidence and flags gaps.',
    shortLabel: 'Verify',
    requiresModel: true,
    required: true,
  },
  timeline_builder: {
    role: 'timeline_builder',
    title: 'Timeline Builder',
    description: 'Builds dated or ordered timelines from verified evidence.',
    shortLabel: 'Timeline',
    requiresModel: true,
    required: true,
  },
  citation_manager: {
    role: 'citation_manager',
    title: 'Citation Manager',
    description: 'Maintains the source index and enforces citation consistency.',
    shortLabel: 'Citations',
    requiresModel: true,
    required: true,
  },
  report_writer: {
    role: 'report_writer',
    title: 'Report Writer',
    description: 'Produces the final cited research report for chat.',
    shortLabel: 'Report',
    requiresModel: true,
    required: true,
  },
  knowledge_graph: {
    role: 'knowledge_graph',
    title: 'Knowledge Graph Memory',
    description: 'Tracks entities, claims, open questions, and evidence links for the run.',
    shortLabel: 'Memory',
    requiresModel: true,
    required: true,
  },
};

const DEEP_RESEARCH_LAYOUT: Array<{ role: CollabNodeRole; position: { x: number; y: number } }> = [
  { role: 'start', position: { x: 40, y: 220 } },
  { role: 'orchestrator', position: { x: 250, y: 220 } },
  { role: 'planner', position: { x: 470, y: 220 } },
  { role: 'web_research', position: { x: 720, y: 70 } },
  { role: 'academic_research', position: { x: 720, y: 200 } },
  { role: 'code_analysis', position: { x: 720, y: 330 } },
  { role: 'knowledge_graph', position: { x: 980, y: 70 } },
  { role: 'source_verification', position: { x: 980, y: 200 } },
  { role: 'timeline_builder', position: { x: 980, y: 330 } },
  { role: 'citation_manager', position: { x: 1230, y: 140 } },
  { role: 'report_writer', position: { x: 1230, y: 300 } },
  { role: 'output', position: { x: 1480, y: 220 } },
];

const getFallbackEditorModelId = (models: AIModel[], preferredModelId?: string | null) => {
  return models.find((model) => model.id !== preferredModelId)?.id || preferredModelId || models[0]?.id || null;
};

const pickRoleModelId = (models: AIModel[], preferredModelId: string | null | undefined, usedModelIds: Set<string>) => {
  const preferred = preferredModelId && models.find((model) => model.id === preferredModelId);
  if (preferred && !usedModelIds.has(preferred.id)) {
    usedModelIds.add(preferred.id);
    return preferred.id;
  }

  const nextUnused = models.find((model) => !usedModelIds.has(model.id));
  if (nextUnused) {
    usedModelIds.add(nextUnused.id);
    return nextUnused.id;
  }

  return preferredModelId || models[0]?.id || null;
};

export const getCollabRoleDefinition = (role: CollabNodeRole) => ROLE_DEFINITIONS[role];
export const getCollabRoleTitle = (role: CollabNodeRole) => ROLE_DEFINITIONS[role]?.title || role;
export const getCollabRoleDescription = (role: CollabNodeRole) => ROLE_DEFINITIONS[role]?.description || '';
export const isResearchWorkflow = (config: CollabConfig | null | undefined) => config?.workflow === 'deep_research';

export const createDefaultStandardCollabConfig = (models: AIModel[], preferredModelId?: string | null): CollabConfig => {
  const drafterModelId = preferredModelId || models[0]?.id || null;
  const editorModelId = getFallbackEditorModelId(models, drafterModelId);

  return {
    version: 2,
    workflow: 'standard',
    nodes: [
      { id: 'start', role: 'start', position: { x: 40, y: 160 } },
      { id: 'drafter-1', role: 'drafter', modelId: drafterModelId, position: { x: 290, y: 90 } },
      { id: 'editor', role: 'editor', modelId: editorModelId, position: { x: 560, y: 160 } },
      { id: 'output', role: 'output', position: { x: 820, y: 160 } },
    ],
  };
};

export const createDefaultDeepResearchConfig = (models: AIModel[], preferredModelId?: string | null): CollabConfig => {
  const usedModelIds = new Set<string>();

  return {
    version: 2,
    workflow: 'deep_research',
    nodes: DEEP_RESEARCH_LAYOUT.map(({ role, position }) => ({
      id: role,
      role,
      modelId: ROLE_DEFINITIONS[role].requiresModel ? pickRoleModelId(models, preferredModelId, usedModelIds) : null,
      position,
    })),
  };
};

export const createDefaultCollabConfig = (
  models: AIModel[],
  preferredModelId?: string | null,
  workflow: CollabWorkflow = 'standard'
): CollabConfig => {
  return workflow === 'deep_research'
    ? createDefaultDeepResearchConfig(models, preferredModelId)
    : createDefaultStandardCollabConfig(models, preferredModelId);
};

const normalizeStandardNodes = (nodes: CollabNodeConfig[], fallback: CollabConfig): CollabNodeConfig[] => {
  const roleMap = new Map(nodes.map((node) => [node.role === 'drafter' ? node.id : node.role, node]));
  const drafterNodes = nodes.filter((node) => node.role === 'drafter');

  return [
    roleMap.get('start') || fallback.nodes[0],
    ...(drafterNodes.length > 0 ? drafterNodes : [fallback.nodes[1]]),
    roleMap.get('editor') || fallback.nodes[2],
    roleMap.get('output') || fallback.nodes[3],
  ];
};

const normalizeDeepResearchNodes = (nodes: CollabNodeConfig[], fallback: CollabConfig): CollabNodeConfig[] => {
  const nodeMap = new Map(nodes.map((node) => [node.role, node]));
  return DEEP_RESEARCH_LAYOUT.map(({ role }, index) => {
    const existing = nodeMap.get(role);
    const fallbackNode = fallback.nodes[index];
    return existing
      ? {
          ...fallbackNode,
          ...existing,
          position: existing.position || fallbackNode.position,
        }
      : fallbackNode;
  });
};

export const normalizeCollabConfig = (
  config: Partial<CollabConfig> | null | undefined,
  models: AIModel[],
  preferredModelId?: string | null
): CollabConfig => {
  const workflow: CollabWorkflow = config?.workflow === 'deep_research' ? 'deep_research' : 'standard';
  const fallback = createDefaultCollabConfig(models, preferredModelId, workflow);

  if (!config || !Array.isArray(config.nodes) || config.nodes.length === 0) {
    return fallback;
  }

  return {
    version: 2,
    workflow,
    nodes: workflow === 'deep_research'
      ? normalizeDeepResearchNodes(config.nodes as CollabNodeConfig[], fallback)
      : normalizeStandardNodes(config.nodes as CollabNodeConfig[], fallback),
  };
};

export const getCollabDrafterNodes = (config: Partial<CollabConfig> | null | undefined) =>
  (config?.nodes || []).filter((node) => node.role === 'drafter');

export const getCollabEditorNode = (config: Partial<CollabConfig> | null | undefined) =>
  (config?.nodes || []).find((node) => node.role === 'editor') || null;

export const getDeepResearchNodes = (config: Partial<CollabConfig> | null | undefined) =>
  (config?.nodes || []).filter((node) => DEEP_RESEARCH_ROLE_ORDER.includes(node.role as Exclude<DeepResearchMessageRole, 'user_prompt'>));

export const getRequiredDeepResearchRoles = () =>
  DEEP_RESEARCH_ROLE_ORDER.filter((role) => ROLE_DEFINITIONS[role].required);

export const getModelNameById = (models: AIModel[], modelId?: string | null) => {
  if (!modelId) return 'Unassigned';
  return models.find((model) => model.id === modelId)?.name || 'Unavailable model';
};

export const validateCollabConfig = (config: Partial<CollabConfig> | null | undefined, models: AIModel[]) => {
  const errors: string[] = [];
  const normalizedWorkflow: CollabWorkflow = config?.workflow === 'deep_research' ? 'deep_research' : 'standard';
  const nodes = (config?.nodes || []) as CollabNodeConfig[];
  const availableIds = new Set(models.map((model) => model.id));

  const nodeIds = new Set<string>();
  nodes.forEach((node) => {
    if (nodeIds.has(node.id)) {
      errors.push('Each node must have a unique id.');
    }
    nodeIds.add(node.id);

    if (ROLE_DEFINITIONS[node.role]?.requiresModel) {
      if (!node.modelId) {
        errors.push(`Choose a model for ${getCollabRoleTitle(node.role)}.`);
      } else if (!availableIds.has(node.modelId)) {
        errors.push(`The saved model for ${getCollabRoleTitle(node.role)} is no longer available.`);
      }
    }
  });

  if (normalizedWorkflow === 'deep_research') {
    const roleMap = new Map(nodes.map((node) => [node.role, node]));
    getRequiredDeepResearchRoles().forEach((role) => {
      if (!roleMap.has(role)) {
        errors.push(`${getCollabRoleTitle(role)} is required for Deep Research.`);
      }
    });

    if (!nodes.find((node) => node.role === 'output')) {
      errors.push('A final output node is required.');
    }
  } else {
    const drafters = nodes.filter((node) => node.role === 'drafter');
    const editor = nodes.find((node) => node.role === 'editor');

    if (!editor) {
      errors.push('An editor node is required.');
    }

    if (drafters.length === 0) {
      errors.push('Add at least one drafter node.');
    }

    const emptyDrafters = drafters.filter((node) => !node.modelId);
    if (emptyDrafters.length > 1) {
      errors.push('Remove extra unassigned drafter nodes before saving.');
    }
  }

  return {
    isValid: errors.length === 0,
    errors: Array.from(new Set(errors)),
  };
};

export const getCollabSummary = (config: Partial<CollabConfig> | null | undefined, models: AIModel[]) => {
  const workflow: CollabWorkflow = config?.workflow === 'deep_research' ? 'deep_research' : 'standard';

  if (workflow === 'deep_research') {
    const researchNodes = getDeepResearchNodes(config);
    const participantSummaries = researchNodes.map((node) => ({
      role: node.role,
      label: getCollabRoleTitle(node.role),
      modelName: getModelNameById(models, node.modelId),
      required: ROLE_DEFINITIONS[node.role].required,
    }));

    return {
      workflow,
      label: `Deep Research · ${participantSummaries.length} agents`,
      description: 'Multi-agent research, verification, citations, and final report',
      drafterModelNames: [],
      editorModelName: 'Managed by Report Writer',
      participantSummaries,
    };
  }

  const drafters = getCollabDrafterNodes(config);
  const editor = getCollabEditorNode(config);

  return {
    workflow,
    label: `${drafters.length} drafter${drafters.length === 1 ? '' : 's'} → 1 editor`,
    description: 'Independent drafts are merged into one edited answer',
    drafterModelNames: drafters.map((node) => getModelNameById(models, node.modelId)),
    editorModelName: getModelNameById(models, editor?.modelId),
    participantSummaries: [
      ...drafters.map((node, index) => ({
        role: node.role,
        label: `Drafter ${index + 1}`,
        modelName: getModelNameById(models, node.modelId),
        required: true,
      })),
      {
        role: 'editor' as const,
        label: 'Editor',
        modelName: getModelNameById(models, editor?.modelId),
        required: true,
      },
    ],
  };
};

export const getNodeSettingsSelectable = (role: CollabNodeRole) => ROLE_DEFINITIONS[role]?.requiresModel;

export const getCollabCanvasTitle = (workflow: CollabWorkflow) =>
  workflow === 'deep_research' ? 'Research pipeline' : 'Collab graph';

export const getCollabCanvasDescription = (workflow: CollabWorkflow) =>
  workflow === 'deep_research'
    ? 'Prompt → orchestration → research → verification → citations → report'
    : 'Prompt → drafter(s) → editor → final output';

export const getCollabRoleOrder = (workflow: CollabWorkflow) =>
  workflow === 'deep_research' ? DEEP_RESEARCH_ROLE_ORDER : STANDARD_NODE_ORDER;
